import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useOrganizationDeletionImpact } from '@/hooks/useOrganization';
import { useArchiveOrganization } from '@/hooks/useOrganizationActions';
import type { OrganizationDeletionImpact } from '@octave/contracts';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useState } from 'react';

/** Rows of the impact recap, in decreasing order of what hurts to lose. */
const IMPACT_ROWS: Array<{
  key: keyof OrganizationDeletionImpact;
  label: string;
}> = [
  { key: 'mobileLines', label: 'Lignes mobiles' },
  { key: 'sites', label: 'Centres de facturation' },
  { key: 'members', label: 'Utilisateurs' },
  { key: 'pendingInvitations', label: 'Invitations en attente' },
  { key: 'orders', label: 'Commandes' },
  { key: 'invoices', label: 'Factures' },
  { key: 'checkouts', label: 'Paniers' },
  { key: 'customPlans', label: 'Forfaits négociés' },
  { key: 'consumptionAlerts', label: 'Alertes de consommation' },
];

export function DeleteOrganizationDialog({
  organizationId,
  organizationName,
  isOpen,
  onClose,
}: {
  organizationId: string;
  organizationName: string;
  isOpen: boolean;
  onClose: () => void;
}) {
  const [confirmationName, setConfirmationName] = useState('');
  const { data: impact, isLoading } =
    useOrganizationDeletionImpact(organizationId);
  const { mutate: archiveOrganization, isPending } = useArchiveOrganization();

  const nameMatches = confirmationName.trim() === organizationName.trim();

  const handleClose = () => {
    setConfirmationName('');
    onClose();
  };

  const handleConfirm = () => {
    archiveOrganization(
      { organizationId, confirmationName },
      { onSuccess: handleClose }
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Supprimer {organizationName} ?</DialogTitle>
          <DialogDescription>
            L'organisation et tout ce qui lui est rattaché disparaîtront
            d'Octave. Les données restent en base et la suppression peut être
            annulée.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-2">
            {[1, 2, 3, 4].map((row) => (
              <Skeleton key={row} className="h-6 w-full" />
            ))}
          </div>
        ) : (
          impact && (
            <div className="space-y-4">
              {impact.activeMobileLines > 0 && (
                <div className="flex gap-3 rounded-xl bg-red-50 px-4 py-3">
                  <AlertTriangle className="h-4 w-4 shrink-0 text-red-600 mt-0.5" />
                  <p className="text-sm text-red-700">
                    <span className="font-semibold">
                      {impact.activeMobileLines} ligne
                      {impact.activeMobileLines > 1 ? 's' : ''} encore active
                      {impact.activeMobileLines > 1 ? 's' : ''}
                    </span>{' '}
                    chez l'opérateur. La suppression ne les résilie pas, elles
                    continueront d'être facturées.
                  </p>
                </div>
              )}

              <div className="rounded-xl bg-slate-50 px-4 py-3">
                <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-2">
                  Deviendra inaccessible
                </p>
                <dl className="space-y-1.5">
                  {IMPACT_ROWS.map((row) => (
                    <div key={row.key} className="flex justify-between text-sm">
                      <dt className="text-slate-500">{row.label}</dt>
                      <dd className="font-semibold text-slate-900 tabular-nums">
                        {impact[row.key] as number}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>

              <div className="space-y-1.5">
                <label
                  htmlFor="confirmation-name"
                  className="text-sm text-slate-600"
                >
                  Tapez{' '}
                  <span className="font-semibold text-slate-900">
                    {organizationName}
                  </span>{' '}
                  pour confirmer
                </label>
                <Input
                  id="confirmation-name"
                  value={confirmationName}
                  onChange={(event) => setConfirmationName(event.target.value)}
                  placeholder={organizationName}
                  autoComplete="off"
                />
              </div>
            </div>
          )
        )}

        <DialogFooter className="flex gap-2 justify-end">
          <Button variant="outline" onClick={handleClose} disabled={isPending}>
            Annuler
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!nameMatches || isPending || isLoading}
          >
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Supprimer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
